import {isDefined} from "~/lib/helpers/guards";
import {DateTime} from "luxon";

export enum TimeUnit {
  Second,
  Minute,
  Hours,
  Day,
  Month,
  Year
}

// TimeDuration represents 1 year, 2 months, 4 days...
export class TimeDuration {
  public count: number;
  public unit: TimeUnit;

  static fromPayload(obj: {count: number, unit: TimeUnit}) {
    return new TimeDuration(obj.count, obj.unit);
  }

  constructor(duration: number, unit: TimeUnit) {
    this.count = duration;
    this.unit = unit;
  }

  is(other: TimeDuration){
    if( isDefined(other)) {
      return this.count === other.count && this.unit === other.unit;
    }
    return false;
  }

}


// A time period consists of a start date, and a duration
// ex: 1 year, starting at the first day of this month
// ex: or 1 month starting at the first dayt of this year
export type TimePeriod = {
  type: TimePeriodStartType,
  duration: TimeDuration
}

export function createTimePeriod(type: TimePeriodStartType, count:number, unit:TimeUnit ){
  return { type, duration: new TimeDuration(count, unit)};
}

export enum TimePeriodStartType {
  firstDayOfTheMonth = 0,
  firstDayOfTheYear = 1,
  // timer for a moving period of time from now ( ex: last 5 minutes, last 2 months, etc)
  timer= 2,
  // date for a fixed start date ( ex: 1 year from 2020-02-14)
  date = 3
}

// Represents [start, end] dates
export class TimeRange {
  public start: Date;
  public end: Date;

  static createFromStartDate( start: Date, duration: TimeDuration  ) {

    let params;

    switch (duration.unit) {
      case TimeUnit.Second :
        params = {seconds: duration.count};
        break;
      case TimeUnit.Minute :
        params = {minute: duration.count};
        break;
      case TimeUnit.Hours :
        params = {hours: duration.count};
        break;
      case TimeUnit.Day :
        params = {day: duration.count};
        break;
      case TimeUnit.Month :
        params = {month: duration.count};
        break;
      case TimeUnit.Year :
        params = {year: duration.count};
        break;
    }

    return new TimeRange(start, DateTime.fromJSDate(start).plus(params).toJSDate());

  }

  constructor(start: Date, end: Date) {
    this.start = start;
    this.end = end;
  }
}

// Resolve the given TimePeriodStartType from now
// Exemple, the JS date of the first day of this month
// or the js date of the first day of this year
export class TimePeriodStartDateResolver {

  static firstOfTheMonth() {
    return DateTime.local().startOf('month').toJSDate();
  }

  static firstOfTheYear() {
    return DateTime.local().startOf('year').toJSDate();
  }

  static timer(duration: TimeDuration) {
    return DateTime.local().minus(buildIntervalParams(duration)).toJSDate();
  }

  // nowJS exposed for testing
  static cronMonth(startDate: Date, nowJS = new Date()) {

    let lastDay = DateTime.fromJSDate(startDate).day;
    let now = DateTime.fromJSDate(nowJS);

    // Start from this month
    let pivotDate = DateTime.fromObject({year: now.year, month: now.month, day: lastDay});
    if (now < pivotDate) pivotDate = pivotDate.minus({month: 1});

    return pivotDate.toJSDate();
  }

  // nowJS exposed for testing
  static cronYear(startDate: Date, nowJS = new Date()) {

    let lastDay = DateTime.fromJSDate(startDate).day;
    let lastMonth = DateTime.fromJSDate(startDate).month;

    let now = DateTime.fromJSDate(nowJS);

    // Pivot date of this year
    let pivotDate = DateTime.fromObject({year: now.year, month: lastMonth, day: lastDay});
    if (now < pivotDate) pivotDate = pivotDate.minus({year: 1});

    return pivotDate.toJSDate();
  }

}

function buildIntervalParams(duration: TimeDuration) {
  switch (duration.unit) {
    case TimeUnit.Second :
      return {seconds: duration.count};
    case TimeUnit.Minute :
      return {minutes: duration.count};
    case TimeUnit.Hours :
      return {hours: duration.count};
    case TimeUnit.Day :
      return {days: duration.count};
    case TimeUnit.Month :
      return {months: duration.count};
    case TimeUnit.Year :
      return {years: duration.count};
  }
}
